import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, X, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage } from '@/i18n/LanguageContext';

export default function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const { language } = useLanguage();
  const isTr = language === 'tr';
  const L = isTr ? {
    addHome: 'Ana Ekrana Ekle', installApp: 'Uygulamayı Yükle',
    safariMenu: 'Safari menüsünden "Ana Ekrana Ekle" seçin',
    fastAccess: 'Hızlı erişim için uygulamayı yükleyin',
    step1: '1. Safari menüsüne (□↑) dokunun',
    step2: '2. "Ana Ekrana Ekle" seçin',
    step3: '3. "Ekle" butonuna dokunun',
    install: 'Yükle',
  } : {
    addHome: 'Add to Home Screen', installApp: 'Install App',
    safariMenu: 'Tap the Safari menu and choose "Add to Home Screen"',
    fastAccess: 'Install the app for fast access',
    step1: '1. Tap the Safari menu (□↑)',
    step2: '2. Select "Add to Home Screen"',
    step3: '3. Tap "Add"',
    install: 'Install',
  };

  useEffect(() => {
    // Check if already installed
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);
    
    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);
    
    // Listen for beforeinstallprompt event (Android/Chrome)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt after 3 seconds if not dismissed before
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    
    // Show iOS prompt if not dismissed
    if (iOS && !standalone) {
      const dismissed = localStorage.getItem('pwa-prompt-dismissed');
      if (!dismissed) {
        setTimeout(() => setShowPrompt(true), 3000);
      }
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setShowPrompt(false);
      }
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', 'true');
  };

  // Don't show if already installed
  if (isStandalone) return null;

  return (
    <AnimatePresence>
      {showPrompt && (
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 100 }}
          className="fixed bottom-4 left-4 right-4 z-50 md:left-auto md:right-4 md:w-96"
        >
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 border border-cyan-500/30 rounded-2xl p-4 shadow-2xl shadow-cyan-500/10">
            <button
              onClick={handleDismiss}
              aria-label="Close install prompt"
              className="absolute top-3 right-3 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="flex items-start gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center flex-shrink-0">
                <Smartphone className="w-7 h-7 text-white" />
              </div>
              
              <div className="flex-1">
                <h3 className="font-bold text-white mb-1">
                  {isIOS ? L.addHome : L.installApp}
                </h3>
                <p className="text-sm text-slate-400 mb-3">
                  {isIOS ? L.safariMenu : L.fastAccess}
                </p>
                
                {isIOS ? (
                  <div className="text-xs text-slate-500 space-y-1">
                    <p>{L.step1}</p>
                    <p>{L.step2}</p>
                    <p>{L.step3}</p>
                  </div>
                ) : (
                  <Button
                    onClick={handleInstall}
                    className="bg-cyan-600 hover:bg-cyan-500 text-white w-full"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {L.install}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
